import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Notification from '../components/common/Notification';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';

interface DashboardStats {
  total_appointments: number;
  pending_appointments: number;
  incomplete_appointments: number;
  completed_appointments: number;
  cancelled_appointments: number;
  follow_up_in_progress?: number;
  follow_up_incomplete?: number;
}

interface Appointment {
  id: number;
  user_id: number;
  service_id: number;
  status: string;
  step_completed: number;
  appointment_date?: string;
  appointment_time?: string;
  tracking_code?: string;
  detail_locked_by_admin_id?: number | null;
  detail_locked_by_username?: string | null;
  detail_lock_expires_at?: string | null;
  created_at: string;
  user?: {
    full_name: string;
    phone: string;
  };
  service?: {
    name: string;
  };
}

const isIncompleteAppointment = (apt: Appointment) => apt.status === 'pending';

/** تعداد ثابت ردیف در هر صفحهٔ داشبورد */
const ITEMS_PER_PAGE = 100;

/** فقط نوبت‌های ۲۴ ساعت اخیر در لیست و آمار داشبورد */
const DASHBOARD_SINCE_HOURS = 24;
const dashboardSinceHoursQuery = `since_hours=${DASHBOARD_SINCE_HOURS}`;

const normalizeAppointmentStatus = (status: string | undefined): string =>
  String(status || '').trim().toLowerCase();

/** تطابق تب داشبورد با وضعیت واقعی نوبت (همان منطق برچسب UI) */
const appointmentMatchesTab = (apt: Appointment, tab: string): boolean => {
  const st = normalizeAppointmentStatus(apt.status);
  if (tab === 'pending') return st === 'in_progress';
  if (tab === 'incomplete') return st === 'pending';
  if (tab === 'completed') return st === 'completed';
  if (tab === 'cancelled') return st === 'cancelled';
  return true;
};

const DASHBOARD_CLIENT_FILTER_TABS = new Set([
  'pending',
  'incomplete',
  'completed',
  'cancelled',
]);

const usesDashboardClientFilter = (tab: string) => DASHBOARD_CLIENT_FILTER_TABS.has(tab);

/**
 * همه تب‌های فیلترشده: همان درخواست «همه (۲۴ ساعت)» — فیلتر وضعیت سمت کلاینت.
 * list_filter سرور باگ داشت و جدیدترین نوبت‌ها را از تب خودشان حذف می‌کرد.
 */
const buildDashboardAppointmentsQuery = (
  skip: number,
  limit: number,
  searchQ: string,
) => {
  const params = new URLSearchParams();
  params.set('skip', String(skip));
  params.set('limit', String(limit));
  params.set('since_hours', String(DASHBOARD_SINCE_HOURS));
  if (searchQ) {
    params.set('q', searchQ);
  }
  return params.toString();
};

const buildStaleFollowUpQuery = (limit: number) => {
  const params = new URLSearchParams();
  params.set('skip', '0');
  params.set('limit', String(limit));
  return params.toString();
};

const getDashboardTabStatCount = (stats: DashboardStats | null, filterStatus: string): number => {
  if (!stats) return 0;
  if (filterStatus === 'pending') return stats.pending_appointments ?? 0;
  if (filterStatus === 'incomplete') return stats.incomplete_appointments ?? 0;
  if (filterStatus === 'completed') return stats.completed_appointments ?? 0;
  if (filterStatus === 'cancelled') return stats.cancelled_appointments ?? 0;
  return stats.total_appointments ?? 0;
};

const isFollowUpStale = (createdAt: string) =>
  Date.now() - new Date(createdAt).getTime() > DASHBOARD_SINCE_HOURS * 60 * 60 * 1000;

/**
 * فهرست شمارهٔ صفحه‌ها برای دکمه‌ها؛ وقتی تعداد صفحات زیاد است با … خلاصه می‌شود.
 */
const buildPageList = (total: number, current: number): (number | 'ellipsis')[] => {
  if (total <= 1) return [1];
  if (total <= 9) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const s = new Set<number>();
  s.add(1);
  s.add(total);
  for (let i = current - 2; i <= current + 2; i++) {
    if (i >= 1 && i <= total) s.add(i);
  }
  const sorted = Array.from(s).sort((a, b) => a - b);
  const result: (number | 'ellipsis')[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev > 0 && p - prev > 1) {
      result.push('ellipsis');
    }
    result.push(p);
    prev = p;
  }
  return result;
};

// کامپوننت جدول نوبت‌ها
const AppointmentsTable: React.FC<{
  appointments: Appointment[];
  onStatusUpdate: (id: number, status: string) => void;
  onViewDetails: (id: number) => void;
  markStaleRows?: boolean;
}> = ({ appointments, onStatusUpdate, onViewDetails, markStaleRows = false }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-fixed divide-y divide-gray-200">
        <colgroup>
          <col className="w-24" />
          <col className="w-[45%]" />
          <col className="w-40" />
          <col className="w-32" />
        </colgroup>
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              شماره نوبت
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              نام
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              وضعیت
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              عملیات
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {appointments.map((appointment) => {
            const stale = markStaleRows && isFollowUpStale(appointment.created_at);
            return (
            <tr
              key={appointment.id}
              className={`h-20 ${stale ? 'bg-red-50/70 border-r-2 border-red-500' : ''}`}
            >
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-24 align-middle">
                #{appointment.tracking_code || (1000 + appointment.id)}
              </td>
              <td className="px-6 py-3 whitespace-nowrap align-middle">
                <div className="h-9 text-sm font-medium text-gray-900 flex items-center gap-2 min-w-0">
                  <span className="truncate block max-w-[180px]">{appointment.user?.full_name}</span>
                  {appointment.detail_locked_by_username && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] leading-none font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200 max-w-[130px] shrink-0">
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v2H5a1 1 0 00-1 1v6a1 1 0 001 1h10a1 1 0 001-1V9a1 1 0 00-1-1h-1V6a4 4 0 00-4-4zm-2 6V6a2 2 0 114 0v2H8z" clipRule="evenodd" />
                      </svg>
                      <span className="truncate block">
                        {appointment.detail_locked_by_username}
                      </span>
                    </span>
                  )}
                </div>
                <div className="h-5 text-sm text-gray-500 truncate">
                  {appointment.user?.phone}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap align-middle">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  appointment.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                    appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                  }`}>
                  {appointment.status === 'pending' ? 'در انتظار' :
                    appointment.status === 'in_progress' ? 'در حال انجام' :
                      appointment.status === 'completed' ? 'تکمیل شده' :
                        'لغو شده'}
                </span>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium align-middle">
                <button
                  onClick={() => onViewDetails(appointment.id)}
                  className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md text-sm font-medium"
                >
                  جزئیات
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [betweenPatients, setBetweenPatients] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all'); // فیلتر وضعیت
  const [listSearchInput, setListSearchInput] = useState('');
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [listSearchTotal, setListSearchTotal] = useState<number | null>(null);
  /** وقتی تب داشبورد غیر از «همه» است؛ از هدر X-Total-Count (فیلتر سمت سرور) */
  const [filteredTotalCount, setFilteredTotalCount] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);
  const lastAppointmentIdRef = useRef<number | null>(null); // آخرین ID رزرو برای تشخیص رزرو جدید
  const lastIncompleteCountRef = useRef<number>(0); // تعداد ناتمام‌ها برای تشخیص تغییر
  const lastTotalCountRef = useRef<number>(0); // تعداد کل نوبت‌ها برای تشخیص تغییر
  const appointmentsStateRef = useRef<Map<number, {
    step_completed: number;
    status: string;
    service_id?: number | null;
    detail_locked_by_admin_id?: number | null;
    detail_lock_expires_at?: string | null;
    detail_locked_by_username?: string | null;
  }>>(new Map()); // نگه‌داری state نوبت‌ها برای تشخیص تغییرات
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // ref برای نگه‌داری interval
  const fetchDashboardDataRef = useRef<
    ((
      showNotification?: boolean,
      page?: number,
      pageSize?: number,
      silent?: boolean,
    ) => Promise<void>) | null
  >(null);
  const currentPageRef = useRef<number>(1);
  const listSearchQueryRef = useRef<string>('');
  const filterStatusRef = useRef<string>(filterStatus);
  const lastTabStatsCountRef = useRef<Record<string, number>>({});
  const fetchGenerationRef = useRef(0);

  useEffect(() => {
    filterStatusRef.current = filterStatus;
  }, [filterStatus]);

  const effectiveListTotal = useMemo(() => {
    if (listSearchQuery.trim()) {
      return listSearchTotal ?? 0;
    }
    if (filterStatus !== 'all') {
      return filteredTotalCount ?? 0;
    }
    return stats?.total_appointments ?? 0;
  }, [
    listSearchQuery,
    listSearchTotal,
    filterStatus,
    filteredTotalCount,
    stats?.total_appointments,
  ]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(effectiveListTotal / ITEMS_PER_PAGE)),
    [effectiveListTotal],
  );

  useEffect(() => {
    setCurrentPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  useEffect(() => {
    const flash = sessionStorage.getItem('appointment_deleted_flash');
    if (flash) {
      sessionStorage.removeItem('appointment_deleted_flash');
      setNotification({ message: flash, type: 'success' });
    }
  }, []);

  useEffect(() => {
    listSearchQueryRef.current = listSearchQuery;
  }, [listSearchQuery]);

  // لیست تب‌های باز در fetchDashboardData فیلتر می‌شود
  const displayedAppointments = allAppointments;

  // Stats Card Component
  const StatCard = ({
    title,
    value,
    color,
    icon,
    active = false,
    followUpCount = 0,
  }: {
    title: string;
    value: number;
    color: { bg: string; text: string };
    icon: React.ReactNode;
    active?: boolean;
    followUpCount?: number;
  }) => (
    <div
      className={`bg-white rounded-xl shadow-sm border transition-all duration-300 overflow-hidden ${
        active
          ? 'border-blue-300 ring-2 ring-blue-100 shadow-md'
          : 'border-gray-100 hover:shadow-md'
      }`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium mb-1 ${active ? 'text-blue-700' : 'text-gray-500'}`}>{title}</p>
            <h3 className={`text-2xl font-bold ${active ? 'text-blue-900' : 'text-gray-900'}`}>{value}</h3>
          </div>
          <div className={`p-3 rounded-full ${color.bg} ${color.text}`}>
            {icon}
          </div>
        </div>
      </div>
      {followUpCount > 0 && (
        <div className="border-t-2 border-red-500 bg-red-50 px-4 py-2">
          <p className="text-xs font-semibold text-red-700">
            اخطار پیگیری — {followUpCount.toLocaleString('fa-IR')} نوبت قدیمی‌تر از ۲۴ ساعت (در لیست زیر)
          </p>
        </div>
      )}
    </div>
  );

  const fetchDashboardData = useCallback(async (
    showNotification: boolean = true,
    page: number = currentPageRef.current,
    pageSize: number = ITEMS_PER_PAGE,
    silent: boolean = false,
  ) => {
    const fetchGeneration = ++fetchGenerationRef.current;
    const fetchFilter = filterStatus;
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      // دریافت آمار
      const statsResponse = await fetch(`${apiBaseUrl}/admin/dashboard/stats?${dashboardSinceHoursQuery}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!statsResponse.ok) {
        throw new Error('خطا در دریافت آمار');
      }

      const statsData = await statsResponse.json();
      setStats(statsData);

      const searchQ = listSearchQuery.trim();
      const clientTabFilter = usesDashboardClientFilter(fetchFilter);
      const listPageSize = clientTabFilter ? 500 : pageSize;
      const listQuery = buildDashboardAppointmentsQuery(
        clientTabFilter ? 0 : page,
        listPageSize,
        searchQ,
      );

      // دریافت نوبت‌های صفحه فعلی (با فیلتر تب در سرور تا صفحه‌بندی با تعداد واقعی هم‌خوان باشد)
      const allResponse = await fetch(
        `${apiBaseUrl}/admin/appointments?${listQuery}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (fetchGeneration !== fetchGenerationRef.current || fetchFilter !== filterStatusRef.current) {
        return;
      }

      if (!allResponse.ok) {
        throw new Error('خطا در دریافت نوبت‌ها');
      }

      const allData: Appointment[] = await allResponse.json();

      if (fetchGeneration !== fetchGenerationRef.current || fetchFilter !== filterStatusRef.current) {
        return;
      }

      let rows = allData;
      if (clientTabFilter) {
        rows = allData.filter((apt) => appointmentMatchesTab(apt, fetchFilter));

        const followUpCount =
          fetchFilter === 'pending'
            ? statsData.follow_up_in_progress ?? 0
            : fetchFilter === 'incomplete'
              ? statsData.follow_up_incomplete ?? 0
              : 0;

        if (
          (fetchFilter === 'pending' || fetchFilter === 'incomplete')
          && !searchQ
          && followUpCount > 0
        ) {
          const staleResponse = await fetch(
            `${apiBaseUrl}/admin/appointments?${buildStaleFollowUpQuery(500)}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            },
          );
          if (
            staleResponse.ok
            && fetchGeneration === fetchGenerationRef.current
            && fetchFilter === filterStatusRef.current
          ) {
            const staleData: Appointment[] = await staleResponse.json();
            const staleRows = staleData.filter(
              (apt) =>
                appointmentMatchesTab(apt, fetchFilter) && isFollowUpStale(apt.created_at),
            );
            const byId = new Map(rows.map((apt) => [apt.id, apt]));
            staleRows.forEach((apt) => {
              if (!byId.has(apt.id)) {
                byId.set(apt.id, apt);
              }
            });
            rows = Array.from(byId.values()).sort((a, b) => b.id - a.id);
          }
        }
      }

      const totalHeader = allResponse.headers.get('X-Total-Count');
      const parsed =
        totalHeader != null && totalHeader !== '' ? parseInt(totalHeader, 10) : NaN;
      const countVal = Number.isFinite(parsed) ? parsed : null;
      const tabTotal = clientTabFilter ? rows.length : countVal;
      if (searchQ) {
        setListSearchTotal(tabTotal !== null ? tabTotal : rows.length);
        setFilteredTotalCount(null);
      } else if (tabTotal !== null) {
        setListSearchTotal(null);
        setFilteredTotalCount(tabTotal);
      } else {
        setListSearchTotal(null);
        setFilteredTotalCount(null);
      }

      // بررسی رزرو جدید (فقط اگر قبلاً داده‌ای داشتیم و showNotification true باشد)
      if (showNotification && lastAppointmentIdRef.current !== null && rows.length > 0) {
        const latestAppointment = rows[0];
        if (latestAppointment.id > lastAppointmentIdRef.current) {
          // رزرو جدید پیدا شد
          const newAppointments = rows.filter((apt: Appointment) => apt.id > lastAppointmentIdRef.current!);
          if (newAppointments.length > 0) {
            const userName = latestAppointment.user?.full_name || 'کاربر جدید';
            const serviceName = latestAppointment.service?.name || 'خدمت';
            setNotification({
              message: `رزرو جدید: ${userName} - ${serviceName}`,
              type: 'success'
            });
          }
        }
      }

      // به‌روزرسانی آخرین ID (همیشه)
      if (rows.length > 0) {
        lastAppointmentIdRef.current = rows[0].id;
      } else {
        lastAppointmentIdRef.current = null;
      }

      const incompleteCount = rows.filter((apt: Appointment) => isIncompleteAppointment(apt)).length;
      lastIncompleteCountRef.current = incompleteCount;
      lastTotalCountRef.current = statsData.total_appointments || rows.length;

      appointmentsStateRef.current.clear();
      rows.forEach((apt: Appointment) => {
        appointmentsStateRef.current.set(apt.id, {
          step_completed: apt.step_completed,
          status: apt.status,
          service_id: apt.service_id,
          detail_locked_by_admin_id: apt.detail_locked_by_admin_id ?? null,
          detail_lock_expires_at: apt.detail_lock_expires_at ?? null,
          detail_locked_by_username: apt.detail_locked_by_username ?? null,
        });
      });

      setAllAppointments(rows);

      if (fetchFilter !== 'all') {
        lastTabStatsCountRef.current[fetchFilter] = rows.length;
      }


    } catch (err: any) {
      if (fetchGeneration === fetchGenerationRef.current) {
        setError(err.message);
      }
    } finally {
      if (!silent && fetchGeneration === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [listSearchQuery, filterStatus]);

  // به‌روزرسانی ref برای fetchDashboardData
  useEffect(() => {
    fetchDashboardDataRef.current = fetchDashboardData;
  }, [fetchDashboardData]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useLayoutEffect(() => {
    currentPageRef.current = 1;
    setCurrentPage(1);
    setAllAppointments([]);
    setFilteredTotalCount(null);
  }, [filterStatus, listSearchQuery]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    // هنگام تغییر صفحه یا تب فیلتر، دیتای همان صفحه بارگیری شود
    fetchDashboardData(false, currentPage, ITEMS_PER_PAGE);
  }, [currentPage, filterStatus, fetchDashboardData]);

  // Polling جداگانه - فقط یک بار تنظیم می‌شود
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    // پاک کردن interval قبلی اگر وجود دارد
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // شروع polling بعد از 3 ثانیه
    const timeoutId = setTimeout(() => {
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const currentToken = localStorage.getItem('admin_token');
          if (!currentToken) {
            console.warn('Polling: No token found');
            return;
          }

          const apiBaseUrl = getApiBaseUrl();

          // دریافت stats برای چک کردن تعداد کل نوبت‌ها
          const statsResponse = await fetch(`${apiBaseUrl}/admin/dashboard/stats?${dashboardSinceHoursQuery}`, {
            headers: {
              'Authorization': `Bearer ${currentToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!statsResponse.ok) {
            console.warn('Polling: Failed to fetch stats, status:', statsResponse.status);
            return;
          }

          const statsData = await statsResponse.json();
          const currentTotalCount = statsData.total_appointments || 0;

          // در صفحات غیر از اول، فقط آمار کلی به‌روز شود تا ترتیب صفحه‌بندی به هم نخورد
          if (currentPageRef.current !== 1) {
            setStats(statsData);
            lastTotalCountRef.current = currentTotalCount;
            return;
          }

          if (listSearchQueryRef.current.trim()) {
            setStats(statsData);
            lastTotalCountRef.current = currentTotalCount;
            return;
          }

          if (filterStatusRef.current !== 'all') {
            setStats(statsData);
            const fs = filterStatusRef.current;
            const newTabCount = getDashboardTabStatCount(statsData, fs);
            const prevTabCount = lastTabStatsCountRef.current[fs];
            lastTabStatsCountRef.current[fs] = newTabCount;
            if (prevTabCount === undefined || newTabCount !== prevTabCount) {
              void fetchDashboardDataRef.current?.(false, currentPageRef.current, ITEMS_PER_PAGE, true);
            }
            lastTotalCountRef.current = currentTotalCount;
            return;
          }

          // دریافت 10 رزرو جدید برای چک کردن (بیشتر از 5 تا مطمئن شویم)
          const response = await fetch(`${apiBaseUrl}/admin/appointments?limit=10&${dashboardSinceHoursQuery}`, {
            headers: {
              'Authorization': `Bearer ${currentToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            console.warn('Polling: Failed to fetch appointments, status:', response.status);
            return;
          }

          const newData = await response.json();

          // بررسی رزرو جدید
          if (newData.length > 0) {
            const latestAppointment = newData[0];
            const incompleteCount = newData.filter((apt: Appointment) => isIncompleteAppointment(apt)).length;

            if (lastAppointmentIdRef.current === null) {
              // اولین بار - فقط ID و تعداد ناتمام را ذخیره کن
              lastAppointmentIdRef.current = latestAppointment.id;
              lastIncompleteCountRef.current = incompleteCount;
              lastTotalCountRef.current = currentTotalCount;
            } else {
              // بررسی تغییرات - هم ID و هم تعداد کل
              const hasNewAppointment = latestAppointment.id > lastAppointmentIdRef.current;
              const hasIncompleteChange = incompleteCount !== lastIncompleteCountRef.current;
              const hasTotalCountChange = currentTotalCount > lastTotalCountRef.current;

              // همچنین بررسی می‌کنیم که آیا نوبت‌هایی با ID بالاتر از lastAppointmentIdRef وجود دارند
              const appointmentsWithHigherId = newData.filter((apt: Appointment) => apt.id > lastAppointmentIdRef.current!);
              const hasNewAppointmentsInList = appointmentsWithHigherId.length > 0;

              // بررسی تغییرات در step_completed، status یا service_id نوبت‌های موجود
              let hasStatusChange = false;
              let hasMeaningfulStatusChange = false;
              const changedAppointments: Appointment[] = [];
              newData.forEach((apt: Appointment) => {
                const prevState = appointmentsStateRef.current.get(apt.id);
                if (prevState) {
                  // نوبت قبلاً وجود داشته - بررسی تغییرات
                  // همچنین بررسی می‌کنیم که آیا service_id تغییر کرده (از null به یک مقدار)
                  const prevServiceId = prevState.service_id ?? null;
                  const currentServiceId = apt.service_id ?? null;
                  const serviceIdChanged = prevServiceId !== currentServiceId;
                  const prevLockOwnerId = prevState.detail_locked_by_admin_id ?? null;
                  const currentLockOwnerId = apt.detail_locked_by_admin_id ?? null;
                  const lockOwnerChanged = prevLockOwnerId !== currentLockOwnerId;
                  const prevLockExp = prevState.detail_lock_expires_at ?? null;
                  const currentLockExp = apt.detail_lock_expires_at ?? null;
                  const lockExpiryChanged = prevLockExp !== currentLockExp;
                  const prevLockOwnerName = prevState.detail_locked_by_username ?? null;
                  const currentLockOwnerName = apt.detail_locked_by_username ?? null;
                  const lockOwnerNameChanged = prevLockOwnerName !== currentLockOwnerName;
                  const statusChanged = prevState.step_completed !== apt.step_completed || prevState.status !== apt.status || serviceIdChanged;

                  if (
                    statusChanged
                    || lockOwnerChanged
                    || lockExpiryChanged
                    || lockOwnerNameChanged
                  ) {
                    hasStatusChange = true;
                    changedAppointments.push(apt);
                    if (statusChanged) {
                      hasMeaningfulStatusChange = true;
                    }
                  }
                }
              });

              if (hasNewAppointment || hasNewAppointmentsInList || hasTotalCountChange || hasStatusChange) {
                // تغییرات پیدا شد - نوبت جدید یا تغییر در نوبت‌های موجود
                try {
                  if (hasStatusChange && !hasNewAppointment && !hasNewAppointmentsInList && !hasTotalCountChange) {
                    // فقط تغییر در status یا step_completed - به‌روزرسانی نوبت‌های تغییر یافته

                    setAllAppointments((prev) => {
                      const updated = prev.map((apt) => {
                        const changed = changedAppointments.find(c => c.id === apt.id);
                        if (changed) {
                          // به‌روزرسانی کامل نوبت تغییر یافته - شامل service و service_id
                          return {
                            ...apt,
                            step_completed: changed.step_completed,
                            status: changed.status,
                            service_id: changed.service_id,
                                service: changed.service, // به‌روزرسانی اطلاعات خدمت
                                detail_locked_by_admin_id: changed.detail_locked_by_admin_id ?? null,
                                detail_lock_expires_at: changed.detail_lock_expires_at ?? null,
                                detail_locked_by_username: changed.detail_locked_by_username ?? null,
                          };
                        }
                        return apt;
                      });

                      // به‌روزرسانی state ref
                      changedAppointments.forEach((apt) => {
                        appointmentsStateRef.current.set(apt.id, {
                          step_completed: apt.step_completed,
                          status: apt.status,
                          service_id: apt.service_id,
                          detail_locked_by_admin_id: apt.detail_locked_by_admin_id ?? null,
                          detail_lock_expires_at: apt.detail_lock_expires_at ?? null,
                          detail_locked_by_username: apt.detail_locked_by_username ?? null,
                        });
                      });

                      return updated;
                    });

                    // به‌روزرسانی stats
                    setStats(statsData);
                    lastIncompleteCountRef.current = incompleteCount;
                    lastTotalCountRef.current = currentTotalCount;

                    // نمایش notification فقط برای تغییرات واقعی وضعیت نوبت (نه قفل ورود/خروج)
                    if (hasMeaningfulStatusChange && changedAppointments.length > 0) {
                      const firstChanged = changedAppointments[0];
                      const userName = firstChanged.user?.full_name || 'کاربر';
                      const serviceName = firstChanged.service?.name || 'خدمت';
                      const message = firstChanged.step_completed === 2
                        ? `نوبت تکمیل شد: ${userName} - ${serviceName}`
                        : `نوبت به‌روزرسانی شد: ${userName} - ${serviceName}`;
                      setNotification({
                        message: message,
                        type: 'info'
                      });
                    }
                  } else {
                    // نوبت جدید یا تغییرات دیگر - دریافت نوبت‌های جدید
                    const limit = hasTotalCountChange ? 20 : 10;
                    const newAppointmentsResponse = await fetch(`${apiBaseUrl}/admin/appointments?limit=${limit}&${dashboardSinceHoursQuery}`, {
                      headers: {
                        'Authorization': `Bearer ${currentToken}`,
                        'Content-Type': 'application/json',
                      },
                    });

                    if (newAppointmentsResponse.ok) {
                      const allNewData = await newAppointmentsResponse.json();
                      // فیلتر کردن نوبت‌های جدید
                      let newAppointments: Appointment[] = [];

                      if (hasNewAppointment || hasNewAppointmentsInList) {
                        // اگر ID جدید پیدا شد، از همان استفاده می‌کنیم
                        newAppointments = allNewData.filter((apt: Appointment) => apt.id > lastAppointmentIdRef.current!);
                      } else if (hasTotalCountChange) {
                        // اگر فقط تعداد کل تغییر کرده، باید نوبت‌های جدید را پیدا کنیم
                        const moreDataResponse = await fetch(`${apiBaseUrl}/admin/appointments?limit=50&${dashboardSinceHoursQuery}`, {
                          headers: {
                            'Authorization': `Bearer ${currentToken}`,
                            'Content-Type': 'application/json',
                          },
                        });

                        if (moreDataResponse.ok) {
                          const moreData = await moreDataResponse.json();
                          newAppointments = moreData.filter((apt: Appointment) => apt.id > lastAppointmentIdRef.current!);
                        }
                      }

                      // همچنین نوبت‌های تغییر یافته را اضافه می‌کنیم
                      if (hasStatusChange && changedAppointments.length > 0) {
                        // نوبت‌های تغییر یافته که در allNewData هستند را اضافه می‌کنیم
                        const updatedFromNewData = allNewData.filter((apt: Appointment) =>
                          changedAppointments.some(c => c.id === apt.id)
                        );
                        newAppointments = [...newAppointments, ...updatedFromNewData];
                      }

                      if (newAppointments.length > 0) {
                        // اضافه کردن نوبت‌های جدید یا به‌روزرسانی نوبت‌های موجود
                        setAllAppointments((prev) => {
                          // جدا کردن نوبت‌های جدید و نوبت‌های به‌روزرسانی شده
                          const existingIds = new Set(prev.map(apt => apt.id));
                          const uniqueNew = newAppointments.filter((apt: Appointment) => !existingIds.has(apt.id));
                          const updatedExisting = newAppointments.filter((apt: Appointment) => existingIds.has(apt.id));

                          // به‌روزرسانی نوبت‌های موجود - شامل تمام فیلدها
                          let updated = prev.map((apt) => {
                            const updatedApt = updatedExisting.find(u => u.id === apt.id);
                            if (updatedApt) {
                              // به‌روزرسانی کامل نوبت - شامل service و service_id
                              return {
                                ...apt,
                                step_completed: updatedApt.step_completed,
                                status: updatedApt.status,
                                service_id: updatedApt.service_id,
                                service: updatedApt.service, // به‌روزرسانی اطلاعات خدمت
                                detail_locked_by_admin_id: updatedApt.detail_locked_by_admin_id ?? null,
                                detail_lock_expires_at: updatedApt.detail_lock_expires_at ?? null,
                                detail_locked_by_username: updatedApt.detail_locked_by_username ?? null,
                              };
                            }
                            return apt;
                          });

                          // اضافه کردن نوبت‌های جدید
                          const combined = [...uniqueNew, ...updated];
                          const sorted = combined.sort((a, b) => {
                            // مرتب‌سازی بر اساس ID (بزرگتر = جدیدتر)
                            return b.id - a.id;
                          });

                          // به‌روزرسانی state ref
                          newAppointments.forEach((apt) => {
                            appointmentsStateRef.current.set(apt.id, {
                              step_completed: apt.step_completed,
                              status: apt.status,
                              service_id: apt.service_id,
                              detail_locked_by_admin_id: apt.detail_locked_by_admin_id ?? null,
                              detail_lock_expires_at: apt.detail_lock_expires_at ?? null,
                              detail_locked_by_username: apt.detail_locked_by_username ?? null,
                            });
                          });

                          return [...sorted];
                        });

                        // به‌روزرسانی stats
                        const statsResponse = await fetch(`${apiBaseUrl}/admin/dashboard/stats?${dashboardSinceHoursQuery}`, {
                          headers: {
                            'Authorization': `Bearer ${currentToken}`,
                            'Content-Type': 'application/json',
                          },
                        });

                        if (statsResponse.ok) {
                          const updatedStatsData = await statsResponse.json();
                          setStats(updatedStatsData);
                        }

                        // نمایش notification فقط اگر تغییر واقعی نوبت باشد
                        const firstNew = newAppointments[0];
                        const userName = firstNew.user?.full_name || 'کاربر جدید';
                        const serviceName = firstNew.service?.name || 'خدمت';

                        // تشخیص نوع تغییر
                        const isNewAppointment = firstNew.id > (lastAppointmentIdRef.current || 0);
                        const isCompleted = firstNew.step_completed === 2;
                        const message = isNewAppointment
                          ? `رزرو جدید: ${userName} - ${serviceName}`
                          : isCompleted
                            ? `نوبت تکمیل شد: ${userName} - ${serviceName}`
                            : `نوبت به‌روزرسانی شد: ${userName} - ${serviceName}`;

                        if (isNewAppointment || hasMeaningfulStatusChange) {
                          setNotification({
                            message: message,
                            type: isNewAppointment ? 'success' : 'info'
                          });
                        }

                        // به‌روزرسانی ref
                        if (newAppointments.length > 0) {
                          const newestId = Math.max(...newAppointments.map(apt => apt.id));
                          lastAppointmentIdRef.current = newestId;
                        } else {
                          lastAppointmentIdRef.current = latestAppointment.id;
                        }
                        lastIncompleteCountRef.current = incompleteCount;
                        lastTotalCountRef.current = currentTotalCount;
                      } else {
                        lastTotalCountRef.current = currentTotalCount;
                      }
                    }
                  }
                } catch (err) {
                  console.error('Polling: ❌ Error adding/updating appointments:', err);
                }
              } else if (hasIncompleteChange) {
                // فقط تعداد ناتمام‌ها تغییر کرده - فقط stats را به‌روز کن
                try {
                  const statsResponse = await fetch(`${apiBaseUrl}/admin/dashboard/stats?${dashboardSinceHoursQuery}`, {
                    headers: {
                      'Authorization': `Bearer ${currentToken}`,
                      'Content-Type': 'application/json',
                    },
                  });

                  if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    setStats(statsData);
                  }

                  lastIncompleteCountRef.current = incompleteCount;
                  lastTotalCountRef.current = currentTotalCount;
                } catch (err) {
                  console.error('Polling: Error updating stats:', err);
                }
              }
            }
          }
        } catch (err) {
          console.error('Polling: ❌ Error:', err);
        }
      }, 3000); // هر 3 ثانیه یکبار
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []); // فقط یک بار اجرا می‌شود


  const updateAppointmentStatus = async (appointmentId: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();

      // اگر وضعیت "completed" است، ابتدا modal را باز کن
      if (newStatus === 'completed') {
        const appointment = allAppointments.find(apt => apt.id === appointmentId);
        if (appointment) {
          setSelectedAppointment(appointment);
          setShowTimeModal(true);
          return; // منتظر بمان تا کاربر زمان را تنظیم کند
        }
      }

      const response = await fetch(`${apiBaseUrl}/admin/appointments/${appointmentId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('خطا در تغییر وضعیت');
      }

      // Refresh data after status update
      await fetchDashboardData();

    } catch (err: any) {
      setError(err.message);
    }
  };


  const closeTimeModal = () => {
    setShowTimeModal(false);
    setSelectedAppointment(null);
    setAppointmentDate('');
    setAppointmentTime('');
    setAppointmentNotes('');
    setBetweenPatients(false);
  };

  const saveAppointmentTime = async () => {
    if (!selectedAppointment || !appointmentDate || !appointmentTime) {
      setError('لطفاً تاریخ و زمان را انتخاب کنید');
      return;
    }

    const appointmentId = selectedAppointment.id;
    const token = localStorage.getItem('admin_token');
    const apiBaseUrl = getApiBaseUrl();

    const readErrorDetail = async (response: Response, fallback: string) => {
      try {
        const data = await response.json();
        if (typeof data?.detail === 'string' && data.detail.trim()) {
          return data.detail;
        }
      } catch {
        /* ignore */
      }
      return fallback;
    };

    try {
      setError(null);

      const lockRes = await fetch(`${apiBaseUrl}/admin/appointments/${appointmentId}/lock`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!lockRes.ok) {
        throw new Error(await readErrorDetail(lockRes, 'امکان قفل نوبت نیست. از صفحهٔ جزئیات همان نوبت اقدام کنید یا بعداً تلاش کنید.'));
      }

      try {
        const completeRes = await fetch(`${apiBaseUrl}/admin/appointments/${appointmentId}/complete`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            appointment_date: appointmentDate,
            appointment_time: appointmentTime,
            notes: appointmentNotes,
            between_patients: betweenPatients,
          }),
        });
        if (!completeRes.ok) {
          throw new Error(await readErrorDetail(completeRes, 'خطا در تکمیل نوبت'));
        }

        await fetchDashboardData();
        closeTimeModal();
      } finally {
        await fetch(`${apiBaseUrl}/admin/appointments/${appointmentId}/lock`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'خطا در تکمیل نوبت';
      setError(msg);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]" style={{ direction: 'ltr' }}>
        <LoadingSpinner text="در حال بارگذاری داشبورد..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                خطا در بارگذاری
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => fetchDashboardData()}
                  className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200"
                >
                  تلاش مجدد
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pendingCardValue =
    filterStatus === 'pending' && filteredTotalCount !== null
      ? filteredTotalCount
      : (stats?.pending_appointments ?? 0);
  const incompleteCardValue =
    filterStatus === 'incomplete' && filteredTotalCount !== null
      ? filteredTotalCount
      : (stats?.incomplete_appointments ?? 0);
  const completedCardValue =
    filterStatus === 'completed' && filteredTotalCount !== null
      ? filteredTotalCount
      : (stats?.completed_appointments ?? 0);
  const cancelledCardValue =
    filterStatus === 'cancelled' && filteredTotalCount !== null
      ? filteredTotalCount
      : (stats?.cancelled_appointments ?? 0);

  return (
    <>
      {/* بیرون از space-y تا margin بین فرزندها با آمدن/رفتن نوتیف جابه‌جا نشود */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
          duration={5000}
        />
      )}
    <div className="space-y-6">

      {/* Stats Cards — ۲۴ ساعت اخیر */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <div onClick={() => setFilterStatus('all')} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
          <StatCard
            title="همه (۲۴ ساعت)"
            value={stats?.total_appointments || 0}
            active={filterStatus === 'all'}
            color={{ bg: 'bg-blue-50', text: 'text-blue-600' }}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            }
          />
        </div>

        <div onClick={() => setFilterStatus('pending')} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
          <StatCard
            title="در حال انجام"
            value={pendingCardValue}
            followUpCount={stats?.follow_up_in_progress ?? 0}
            active={filterStatus === 'pending'}
            color={{ bg: 'bg-blue-50', text: 'text-blue-600' }}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        <div onClick={() => setFilterStatus('incomplete')} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
          <StatCard
            title="در انتظار"
            value={incompleteCardValue}
            followUpCount={stats?.follow_up_incomplete ?? 0}
            active={filterStatus === 'incomplete'}
            color={{ bg: 'bg-yellow-50', text: 'text-yellow-600' }}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        <div onClick={() => setFilterStatus('completed')} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
          <StatCard
            title="تکمیل شده"
            value={completedCardValue}
            active={filterStatus === 'completed'}
            color={{ bg: 'bg-green-50', text: 'text-green-600' }}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        <div onClick={() => setFilterStatus('cancelled')} className="cursor-pointer transform hover:scale-105 transition-transform duration-200">
          <StatCard
            title="لغو شده"
            value={cancelledCardValue}
            active={filterStatus === 'cancelled'}
            color={{ bg: 'bg-red-50', text: 'text-red-600' }}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">لیست نوبت‌ها</h2>
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600">
              {filterStatus === 'pending' || filterStatus === 'incomplete'
                ? '۲۴ ساعت اخیر + نیازمند پیگیری'
                : '۲۴ ساعت گذشته'}
            </span>
          </div>
          <form
            className="relative w-full max-w-md sm:shrink-0"
            onSubmit={(e) => {
              e.preventDefault();
              setListSearchQuery(listSearchInput.trim());
            }}
          >
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400" aria-hidden>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="search"
              value={listSearchInput}
              onChange={(e) => setListSearchInput(e.target.value)}
              placeholder="جستجو — Enter"
              aria-label="جستجوی نوبت"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pr-9 pl-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/25"
              dir="rtl"
              autoComplete="off"
            />
            {(listSearchInput.trim() || listSearchQuery.trim()) && (
              <button
                type="button"
                onClick={() => {
                  setListSearchInput('');
                  setListSearchQuery('');
                }}
                className="absolute inset-y-0 left-2 my-auto rounded p-1 text-gray-400 hover:bg-gray-200/80 hover:text-gray-700"
                title="پاک کردن"
                aria-label="پاک کردن جستجو"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </form>
        </div>

        {displayedAppointments.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">هیچ نوبتی یافت نشد</h3>
            <p className="mt-1 text-sm text-gray-500">
              {listSearchQuery.trim()
                ? 'نتیجه‌ای برای این جستجو یافت نشد؛ عبارت دیگری امتحان کنید یا فیلتر وضعیت را عوض کنید.'
                : filterStatus === 'all' ? 'نوبتی در ۲۴ ساعت گذشته ثبت نشده است.' :
                  filterStatus === 'pending' ? 'نوبتی در حال انجام وجود ندارد.' :
                    filterStatus === 'incomplete' ? 'نوبتی در انتظار وجود ندارد.' :
                      filterStatus === 'completed' ? 'نوبت تکمیل شده‌ای در ۲۴ ساعت گذشته وجود ندارد.' :
                        'نوبت لغو شده‌ای در ۲۴ ساعت گذشته وجود ندارد.'}
            </p>
          </div>
        ) : (
          <AppointmentsTable
            appointments={displayedAppointments}
            onStatusUpdate={updateAppointmentStatus}
            onViewDetails={(id) => navigate(`/admin/appointments/${id}`)}
            markStaleRows={filterStatus === 'pending' || filterStatus === 'incomplete'}
          />
        )}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-100 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500 text-center sm:text-right tabular-nums">
            صفحه {currentPage.toLocaleString('fa-IR')} از {totalPages.toLocaleString('fa-IR')}
          </p>
          <nav
            className="flex flex-wrap items-center justify-center gap-1"
            aria-label="صفحه‌بندی"
            dir="ltr"
          >
            {buildPageList(totalPages, currentPage).map((item, idx) =>
              item === 'ellipsis' ? (
                <span
                  key={`e-${idx}`}
                  className="px-2 py-1.5 text-sm text-gray-400 select-none"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCurrentPage(item)}
                  className={`min-w-[2.25rem] px-2 py-1.5 text-sm rounded-md border tabular-nums transition-colors ${
                    currentPage === item
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.toLocaleString('fa-IR')}
                </button>
              ),
            )}
          </nav>
        </div>
      </div>

      {/* Appointment Time Modal */}
      {showTimeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-96 shadow-xl rounded-xl bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-bold text-gray-900">تعیین زمان نوبت</h3>
                <button onClick={closeTimeModal} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {selectedAppointment && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-gray-700 font-medium">
                    <span className="text-gray-500 ml-1">مراجع:</span> {selectedAppointment.user?.full_name}
                  </p>
                  <p className="text-sm text-gray-700 font-medium mt-1">
                    <span className="text-gray-500 ml-1">خدمت:</span> {selectedAppointment.service?.name}
                  </p>
                </div>
              )}

              <div className="mt-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ مراجعه</label>
                  <PersianDatePicker
                    value={appointmentDate}
                    onChange={setAppointmentDate}
                    placeholder="انتخاب تاریخ"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ساعت مراجعه</label>
                  <div className="relative">
                    <input
                      type="time"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={appointmentTime}
                      onChange={(e) => setAppointmentTime(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={betweenPatients}
                      onChange={(e) => setBetweenPatients(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-800">بین مریض</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 mr-6">
                    در صورت فعال بودن، به پیامک زمان نوبت جملهٔ «دو ساعت زمان انتظار در مرکز…» اضافه می‌شود.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات (اختیاری)</label>
                  <textarea
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    rows={3}
                    value={appointmentNotes}
                    onChange={(e) => setAppointmentNotes(e.target.value)}
                    placeholder="توضیحاتی برای کاربر..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={closeTimeModal}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  انصراف
                </button>
                <button
                  onClick={saveAppointmentTime}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
                >
                  ذخیره و تکمیل
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AdminDashboard;
